import objectMappings from "../../../data/ObjectMappings.json";
import swedishMunicipalitiesRaw from "../../../data/swedish_municipalities.geojson?raw";

function normalizeText(value) {
  return (value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");
}

function normalizeMunicipalityName(value) {
  return normalizeText(value).replace(/kommun$/g, "");
}

function buildAliases(value) {
  const base = normalizeMunicipalityName(value);

  if (!base) {
    return [];
  }

  const aliases = new Set([base]);

  if (base.endsWith("s") && base.length > 1) {
    aliases.add(base.slice(0, -1));
  } else {
    aliases.add(`${base}s`);
  }

  return Array.from(aliases);
}

function parseMunicipalityFeatures(rawValue) {
  try {
    const parsed = JSON.parse(rawValue.replace(/\/\*[\s\S]*?\*\//g, ""));

    return Array.isArray(parsed?.features) ? parsed.features : [];
  } catch {
    return [];
  }
}

function createBounds() {
  return {
    minLat: Number.POSITIVE_INFINITY,
    maxLat: Number.NEGATIVE_INFINITY,
    minLng: Number.POSITIVE_INFINITY,
    maxLng: Number.NEGATIVE_INFINITY,
  };
}

function extendBounds(bounds, ring = []) {
  ring.forEach(([lng, lat]) => {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return;
    }

    bounds.minLat = Math.min(bounds.minLat, lat);
    bounds.maxLat = Math.max(bounds.maxLat, lat);
    bounds.minLng = Math.min(bounds.minLng, lng);
    bounds.maxLng = Math.max(bounds.maxLng, lng);
  });
}

function getGeometryBounds(geometry) {
  if (!geometry?.coordinates) {
    return null;
  }

  const bounds = createBounds();

  if (geometry.type === "Polygon") {
    geometry.coordinates.forEach((ring) => extendBounds(bounds, ring));
  } else if (geometry.type === "MultiPolygon") {
    geometry.coordinates.forEach((polygon) => {
      polygon.forEach((ring) => extendBounds(bounds, ring));
    });
  } else {
    return null;
  }

  if (!Number.isFinite(bounds.minLat) || !Number.isFinite(bounds.minLng)) {
    return null;
  }

  return bounds;
}

function isPointWithinBounds(point, bounds) {
  return (
    bounds &&
    point.lat >= bounds.minLat &&
    point.lat <= bounds.maxLat &&
    point.lng >= bounds.minLng &&
    point.lng <= bounds.maxLng
  );
}

function isPointOnSegment(point, start, end) {
  const epsilon = 1e-10;
  const [x1, y1] = start;
  const [x2, y2] = end;
  const cross = (point.lat - y1) * (x2 - x1) - (point.lng - x1) * (y2 - y1);

  if (Math.abs(cross) > epsilon) {
    return false;
  }

  const dot =
    (point.lng - x1) * (point.lng - x2) + (point.lat - y1) * (point.lat - y2);

  return dot <= epsilon;
}

function isPointInRing(point, ring = []) {
  if (ring.length < 3) {
    return false;
  }

  let isInside = false;

  for (
    let index = 0, previous = ring.length - 1;
    index < ring.length;
    previous = index++
  ) {
    const current = ring[index];
    const prior = ring[previous];

    if (isPointOnSegment(point, prior, current)) {
      return true;
    }

    const [currentLng, currentLat] = current;
    const [previousLng, previousLat] = prior;
    const doesIntersect =
      currentLat > point.lat !== previousLat > point.lat &&
      point.lng <
        ((previousLng - currentLng) * (point.lat - currentLat)) /
          (previousLat - currentLat) +
          currentLng;

    if (doesIntersect) {
      isInside = !isInside;
    }
  }

  return isInside;
}

function isPointInPolygon(point, polygon = []) {
  const [outerRing, ...holes] = polygon;

  if (!outerRing || !isPointInRing(point, outerRing)) {
    return false;
  }

  return !holes.some((ring) => isPointInRing(point, ring));
}

function isPointInGeometry(point, geometry) {
  if (!geometry?.coordinates) {
    return false;
  }

  if (geometry.type === "Polygon") {
    return isPointInPolygon(point, geometry.coordinates);
  }

  if (geometry.type === "MultiPolygon") {
    return geometry.coordinates.some((polygon) =>
      isPointInPolygon(point, polygon)
    );
  }

  return false;
}

function getCoordinateValue(value) {
  return typeof value === "function" ? value() : value;
}

function normalizeCoordinates(location) {
  if (!location) {
    return null;
  }

  const lat = getCoordinateValue(location.lat);
  const lng = getCoordinateValue(location.lng);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  return { lat, lng };
}

function findDropdownMatch(entry, ddKommun = []) {
  return ddKommun.find(
    ({ mch_object, kommun }) =>
      mch_object === entry.Object ||
      normalizeMunicipalityName(kommun) ===
        normalizeMunicipalityName(entry.Municipality)
  );
}

function buildResolvedMatch(entry, ddKommun = [], sourceMunicipality) {
  const dropdownMatch = findDropdownMatch(entry, ddKommun);

  return {
    municipality: dropdownMatch?.kommun || entry.Municipality,
    object: dropdownMatch?.mch_object || entry.Object,
    sourceMunicipality: sourceMunicipality || entry.Municipality,
  };
}

const municipalityIndex = objectMappings.reduce((index, entry) => {
  buildAliases(entry.Municipality).forEach((alias) => {
    if (!index.has(alias)) {
      index.set(alias, entry);
    }
  });

  return index;
}, new Map());

const objectMappingByObject = objectMappings.reduce((index, entry) => {
  index.set(entry.Object, entry);

  return index;
}, new Map());

const municipalityFeatures = parseMunicipalityFeatures(swedishMunicipalitiesRaw)
  .map((feature) => {
    const geometry = feature?.geometry;
    const municipalityId = feature?.properties?.id;
    const objectCode = municipalityId ? `K${municipalityId}` : null;
    const objectMapping = objectCode
      ? objectMappingByObject.get(objectCode)
      : null;
    const bounds = getGeometryBounds(geometry);

    if (!geometry || !objectMapping || !bounds) {
      return null;
    }

    return {
      bounds,
      geometry,
      objectMapping,
    };
  })
  .filter(Boolean);

function getComponentText(component) {
  return (
    component?.longText ||
    component?.long_name ||
    component?.shortText ||
    component?.short_name ||
    ""
  );
}

function getComponentByType(addressComponents, type) {
  return addressComponents.find((component) => component.types?.includes(type));
}

function getFirstComponentText(addressComponents = [], types = []) {
  for (const type of types) {
    const value = getComponentText(getComponentByType(addressComponents, type));

    if (value) {
      return value;
    }
  }

  return "";
}

export function extractAddressFields(addressComponents = []) {
  const route = getFirstComponentText(addressComponents, ["route"]);
  const streetNumber = getFirstComponentText(addressComponents, [
    "street_number",
  ]);
  const fallbackStreetAddress = getFirstComponentText(addressComponents, [
    "premise",
    "establishment",
    "point_of_interest",
  ]);
  const postalCode = getFirstComponentText(addressComponents, ["postal_code"]);
  const postalCodeSuffix = getFirstComponentText(addressComponents, [
    "postal_code_suffix",
  ]);

  return {
    customerAddress:
      [route, streetNumber].filter(Boolean).join(" ") || fallbackStreetAddress,
    customerPostalcode:
      postalCode && postalCodeSuffix
        ? `${postalCode}-${postalCodeSuffix}`
        : postalCode,
    customerCity: getFirstComponentText(addressComponents, [
      "locality",
      "postal_town",
      "sublocality_level_1",
      "administrative_area_level_3",
      "administrative_area_level_2",
    ]),
  };
}

export function extractMunicipalityCandidates(addressComponents = []) {
  const candidateTypes = [
    "locality",
    "postal_town",
    "administrative_area_level_2",
    "administrative_area_level_3",
  ];

  const candidates = candidateTypes
    .map((type) => getComponentByType(addressComponents, type))
    .map(getComponentText)
    .filter(Boolean);

  return Array.from(new Set(candidates));
}

export function resolveMunicipalityMatchByCoordinates(location, ddKommun = []) {
  const point = normalizeCoordinates(location);

  if (!point) {
    return null;
  }

  const municipalityFeature = municipalityFeatures.find(
    ({ bounds, geometry }) =>
      isPointWithinBounds(point, bounds) && isPointInGeometry(point, geometry)
  );

  if (!municipalityFeature) {
    return null;
  }

  return buildResolvedMatch(
    municipalityFeature.objectMapping,
    ddKommun,
    municipalityFeature.objectMapping.Municipality
  );
}

export function resolveMunicipalityMatch(candidates = [], ddKommun = []) {
  for (const candidate of candidates) {
    const match = municipalityIndex.get(normalizeMunicipalityName(candidate));

    if (!match) {
      continue;
    }

    return buildResolvedMatch(match, ddKommun, candidate);
  }

  return null;
}
